import { useState } from 'react';
import { useMsal } from 'src/auth/firebaseMsalCompat';
import { useErrorBoundary } from 'react-error-boundary';
import axios from 'axios';

import { loginRequest } from '../../config/authConfig';
import { getApiHost } from '../../utils/getApiHost';
import type { DisplaySettings } from '../../types/types';

/** The project changed since this page loaded it. Reload before saving again. */
export class ProjectRevisionConflict extends Error {
  constructor() {
    super('This project was updated somewhere else.');
    this.name = 'ProjectRevisionConflict';
  }
}

const isRevisionConflict = (err: unknown): boolean =>
  axios.isAxiosError(err) && err.response?.status === 409;

export const useProjectActions = (isAuthenticated: boolean) => {
  const [loading, setStatusLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { instance } = useMsal();
  const { showBoundary } = useErrorBoundary();

  const renameProject = async (projectId: string, newName: string) => {
    if (!isAuthenticated) return;

    try {
      setError(null);
      setStatusLoading(true);

      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });

      const apiUrl = getApiHost();
      const response = await axios.put(
        `${apiUrl}/project/rename/${projectId}`,
        { new_name: newName },
        {
          headers: {
            Authorization: `Bearer ${authResult.idToken}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      setError(err);
      showBoundary(err);
      throw err; // Re-throw to handle in component
    } finally {
      setStatusLoading(false);
    }
  };

  const deleteProject = async (projectId: string) => {
    if (!isAuthenticated) return;

    try {
      setError(null);
      setStatusLoading(true);

      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });

      const apiUrl = getApiHost();
      const response = await axios.delete(
        `${apiUrl}/project/delete/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${authResult.idToken}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      // No error boundary: a failed delete shouldn't take down the
      // workspace. The caller tells the user.
      setError(err);
      throw err;
    } finally {
      setStatusLoading(false);
    }
  };

  const createProject = async (name: string) => {
    if (!isAuthenticated) throw new Error('User not authenticated');

    try {
      setError(null);
      setStatusLoading(true);

      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });

      const apiUrl = getApiHost();
      const response = await axios.post(
        `${apiUrl}/project/create`,
        { name },
        {
          headers: {
            Authorization: `Bearer ${authResult.idToken}`,
          },
        }
      );

      return response.data;
    } catch (err: any) {
      setError(err);
      showBoundary(err);
      throw err;
    } finally {
      setStatusLoading(false);
    }
  };

  const saveProject = async (
    projectId: string,
    fingeringData: unknown,
    revision?: number
  ) => {
    if (!isAuthenticated) return;

    try {
      setError(null);
      setStatusLoading(true);

      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });

      const apiUrl = getApiHost();
      const response = await axios.put(
        `${apiUrl}/project/update-fingering/${projectId}`,
        { fingeringData, revision },
        {
          headers: {
            Authorization: `Bearer ${authResult.idToken}`,
          },
        }
      );

      return response.data as { revision?: number };
    } catch (err: any) {
      if (isRevisionConflict(err)) {
        const conflict = new ProjectRevisionConflict();
        setError(conflict);
        throw conflict;
      }
      setError(err);
      showBoundary(err);
      throw err; // Re-throw to handle in component
    } finally {
      setStatusLoading(false);
    }
  };

  const savePdfProject = async (
    projectId: string,
    fingeringData: unknown,
    revision?: number
  ) => {
    if (!isAuthenticated) return;

    try {
      setError(null);
      setStatusLoading(true);

      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });

      const apiUrl = getApiHost();
      const response = await axios.put(
        `${apiUrl}/project/update-fingering-pdf/${projectId}`,
        { fingeringData, revision },
        {
          headers: {
            Authorization: `Bearer ${authResult.idToken}`,
          },
        }
      );

      return response.data as { revision?: number };
    } catch (err: any) {
      if (isRevisionConflict(err)) {
        const conflict = new ProjectRevisionConflict();
        setError(conflict);
        throw conflict;
      }
      setError(err);
      showBoundary(err);
      throw err; // Re-throw to handle in component
    } finally {
      setStatusLoading(false);
    }
  };

  const updateDisplaySettings = async (
    projectId: string,
    settings: DisplaySettings,
    displayRevision?: number
  ) => {
    if (!isAuthenticated) return;

    const authResult = await instance.acquireTokenSilent({
      ...loginRequest,
    });

    const apiUrl = getApiHost();
    const response = await axios.put(
      `${apiUrl}/project/${projectId}/display-settings`,
      displayRevision == null ? settings : { ...settings, displayRevision },
      {
        headers: {
          Authorization: `Bearer ${authResult.idToken}`,
        },
      }
    );

    return response.data as { displayRevision?: number };
  };

  return {
    renameProject,
    deleteProject,
    createProject,
    saveProject,
    savePdfProject,
    updateDisplaySettings,
    loading,
    error,
  };
};
